import React from 'react';
import { CheckCircle, Target, Award, TrendingUp } from 'lucide-react';

const About = () => {
  const values = [
    { icon: <CheckCircle className="w-8 h-8" />, title: 'Quality First', description: 'Unwavering commitment to sourcing and delivering premium products' },
    { icon: <Target className="w-8 h-8" />, title: 'Integrity', description: 'Transparent operations and honest communication at every step' },
    { icon: <Award className="w-8 h-8" />, title: 'Reliability', description: 'Consistent delivery and dependable partnerships' },
    { icon: <TrendingUp className="w-8 h-8" />, title: 'Excellence', description: 'Continuous improvement in processes and service standards' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-light tracking-tight text-[#0B1B32] mb-6" data-testid="about-title">
              About Vista Global Trade
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Building bridges between India's finest agricultural products and global markets through structured sourcing and quality-driven execution.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#0B1B32] mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Vista Global Trade brings <strong>25+ years of deep product experience</strong> in agricultural commodities, with a comprehensive understanding of sourcing, quality parameters, and market dynamics.
                </p>
                <p>
                  Our journey has been built on establishing a <strong>robust procurement network</strong> across India's agricultural heartlands, enabling us to source premium quality products that meet stringent international standards.
                </p>
                <p>
                  We've developed a <strong>structured sourcing model</strong> that emphasizes vendor verification, batch-level quality inspection, and comprehensive documentation support for seamless export operations.
                </p>
                <p>
                  Through transparent communication and a partnership-first approach, we've built lasting relationships with businesses across the Middle East, Europe, Asia, and North America.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/6950015/pexels-photo-6950015.jpeg" 
                alt="Global Trade Operations" 
                className="w-full h-[500px] object-cover shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Expertise */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#0B1B32] mb-4">
              Our Expertise
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Decades of experience across the agricultural supply chain
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 border-l-4 border-[#0B1B32]">
              <h3 className="text-xl font-medium text-[#0B1B32] mb-3">Procurement Understanding</h3>
              <p className="text-slate-600 leading-relaxed">
                Deep knowledge of regional variations, seasonal patterns, and supplier ecosystems across India's diverse agricultural landscape.
              </p>
            </div>
            <div className="bg-white p-8 border-l-4 border-[#0B1B32]">
              <h3 className="text-xl font-medium text-[#0B1B32] mb-3">Quality Assurance</h3>
              <p className="text-slate-600 leading-relaxed">
                Systematic quality checks including moisture content, purity levels, grading parameters, and contamination screening.
              </p>
            </div>
            <div className="bg-white p-8 border-l-4 border-[#0B1B32]">
              <h3 className="text-xl font-medium text-[#0B1B32] mb-3">Export Coordination</h3>
              <p className="text-slate-600 leading-relaxed">
                Comprehensive support with documentation, compliance, packaging standards, and logistics coordination for international shipments.
              </p>
            </div>
            <div className="bg-white p-8 border-l-4 border-[#0B1B32]">
              <h3 className="text-xl font-medium text-[#0B1B32] mb-3">Market Intelligence</h3>
              <p className="text-slate-600 leading-relaxed">
                Ongoing monitoring of price trends, quality benchmarks, and regulatory requirements across global markets.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#0B1B32] mb-4">
              Our Values
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              The principles that guide every decision we make
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center" data-testid={`value-${index}`}>
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F0F9FF] text-[#0B1B32] mb-4">
                  {value.icon}
                </div>
                <h3 className="text-xl font-medium text-[#0B1B32] mb-2">{value.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-24 md:py-32 bg-[#0B1B32] text-white">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-6">
              Our Commitment
            </h2>
            <p className="text-slate-200 text-lg leading-relaxed mb-8">
              At Vista Global Trade, we are committed to building long-term partnerships based on quality, integrity, and mutual growth. We don't just supply products — we provide reliable sourcing solutions that help your business thrive in competitive global markets.
            </p>
            <p className="text-slate-300 leading-relaxed">
              Every shipment, every interaction, and every decision reflects our dedication to excellence and our respect for the trust you place in us.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;